import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';

const CustomPreset = definePreset(Lara, {
  primitive: {
    // Define your custom brand colors here
    // Orange-based color palette for branding
    brand: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#ff8533',
      500: '#ff6600', // Primary brand color
      600: '#e55a00',
      700: '#cc5000',
      800: '#b34600',
      900: '#993c00',
      950: '#662800',
    },
    // You can also define secondary/accent colors
    // Teal accent to complement orange
    accent: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6', // Accent color (teal)
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
      950: '#042f2e',
    },
    // Override green colors for success states
    green: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16',
    },
    // Override red colors for danger/error states
    red: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a',
    },
    // Blue colors for info states
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
    // Amber colors for warning states
    amber: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
    // Purple colors for help/special states
    purple: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764',
    },
    // Gray colors for secondary/neutral states
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#030712',
    },
  },
  semantic: {
    // Map the primary color to your brand colors
    primary: {
      50: '{brand.50}',
      100: '{brand.100}',
      200: '{brand.200}',
      300: '{brand.300}',
      400: '{brand.400}',
      500: '{brand.500}',
      600: '{brand.600}',
      700: '{brand.700}',
      800: '{brand.800}',
      900: '{brand.900}',
      950: '{brand.950}',
    },
    // Override specific color mappings for light mode
    colorScheme: {
      light: {
        primary: {
          color: '{brand.500}',
          inverseColor: '#ffffff',
          hoverColor: '{brand.600}',
          activeColor: '{brand.700}',
        },
        highlight: {
          background: '{brand.50}',
          focusBackground: '{brand.100}',
          color: '{brand.700}',
          focusColor: '{brand.800}',
        },
        // You can customize surface colors for backgrounds
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}',
        },
      },
      dark: {
        primary: {
          color: '{brand.400}',
          inverseColor: '{brand.950}',
          hoverColor: '{brand.300}',
          activeColor: '{brand.200}',
        },
        highlight: {
          background: 'color-mix(in srgb, {brand.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {brand.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.95)',
        },
      },
    },
  },
  components: {
    // You can also customize specific components
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.color}',
              hoverBackground: '{primary.hoverColor}',
              activeBackground: '{primary.activeColor}',
              borderColor: '{primary.color}',
              hoverBorderColor: '{primary.hoverColor}',
              activeBorderColor: '{primary.activeColor}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {primary.200}',
              },
            },
            success: {
              background: '{green.500}',
              hoverBackground: '{green.600}',
              activeBackground: '{green.700}',
              borderColor: '{green.500}',
              hoverBorderColor: '{green.600}',
              activeBorderColor: '{green.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {green.200}',
              },
            },
            danger: {
              background: '{red.500}',
              hoverBackground: '{red.600}',
              activeBackground: '{red.700}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.600}',
              activeBorderColor: '{red.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {red.200}',
              },
            },
            info: {
              background: '{blue.500}',
              hoverBackground: '{blue.600}',
              activeBackground: '{blue.700}',
              borderColor: '{blue.500}',
              hoverBorderColor: '{blue.600}',
              activeBorderColor: '{blue.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {blue.200}',
              },
            },
            warn: {
              background: '{amber.500}',
              hoverBackground: '{amber.600}',
              activeBackground: '{amber.700}',
              borderColor: '{amber.500}',
              hoverBorderColor: '{amber.600}',
              activeBorderColor: '{amber.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {amber.200}',
              },
            },
            help: {
              background: '{purple.500}',
              hoverBackground: '{purple.600}',
              activeBackground: '{purple.700}',
              borderColor: '{purple.500}',
              hoverBorderColor: '{purple.600}',
              activeBorderColor: '{purple.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {purple.200}',
              },
            },
            secondary: {
              background: '{gray.100}',
              hoverBackground: '{gray.200}',
              activeBackground: '{gray.300}',
              borderColor: '{gray.100}',
              hoverBorderColor: '{gray.200}',
              activeBorderColor: '{gray.300}',
              color: '{gray.700}',
              hoverColor: '{gray.800}',
              activeColor: '{gray.900}',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {gray.200}',
              },
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: '{primary.color}',
              hoverBackground: '{primary.hoverColor}',
              activeBackground: '{primary.activeColor}',
              borderColor: '{primary.color}',
              hoverBorderColor: '{primary.hoverColor}',
              activeBorderColor: '{primary.activeColor}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem {primary.200}',
              },
            },
            success: {
              background: '{green.500}',
              hoverBackground: '{green.400}',
              activeBackground: '{green.300}',
              borderColor: '{green.500}',
              hoverBorderColor: '{green.400}',
              activeBorderColor: '{green.300}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {green.500}, transparent 80%)',
              },
            },
            danger: {
              background: '{red.500}',
              hoverBackground: '{red.400}',
              activeBackground: '{red.300}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.400}',
              activeBorderColor: '{red.300}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {red.500}, transparent 80%)',
              },
            },
            info: {
              background: '{blue.500}',
              hoverBackground: '{blue.400}',
              activeBackground: '{blue.300}',
              borderColor: '{blue.500}',
              hoverBorderColor: '{blue.400}',
              activeBorderColor: '{blue.300}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {blue.500}, transparent 80%)',
              },
            },
            warn: {
              background: '{amber.500}',
              hoverBackground: '{amber.400}',
              activeBackground: '{amber.300}',
              borderColor: '{amber.500}',
              hoverBorderColor: '{amber.400}',
              activeBorderColor: '{amber.300}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {amber.500}, transparent 80%)',
              },
            },
            help: {
              background: '{purple.500}',
              hoverBackground: '{purple.400}',
              activeBackground: '{purple.300}',
              borderColor: '{purple.500}',
              hoverBorderColor: '{purple.400}',
              activeBorderColor: '{purple.300}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {purple.500}, transparent 80%)',
              },
            },
            secondary: {
              background: '{gray.800}',
              hoverBackground: '{gray.700}',
              activeBackground: '{gray.600}',
              borderColor: '{gray.800}',
              hoverBorderColor: '{gray.700}',
              activeBorderColor: '{gray.600}',
              color: '{gray.100}',
              hoverColor: '{gray.50}',
              activeColor: '{gray.50}',
              focusRing: {
                color: 'transparent',
                shadow: '0 0 0 0.2rem color-mix(in srgb, {gray.500}, transparent 80%)',
              },
            },
          },
        },
      },
    },
    // Example: Customize the menubar component
    menubar: {
      background: '{surface.0}',
      borderColor: '{surface.300}',
      color: '{text.color}',
    },
  },
});

export default CustomPreset;
